import React, { useState } from 'react'
import { NextUIProvider } from '@nextui-org/react'
import { Route, Routes, Navigate, HashRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import Login from './components/Login'
import Dashboard from './components/structure/Dashboard'
import ErrorAlert from './components/ErrorAlert'
import { SubgraphsProvider } from './context/SubgraphsContext'
import { AuthProvider, useAuth } from './context/AuthContext'
import { BookmarksProvider } from './context/BookmarksContext'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retryDelay: 1000,
    },
  },
})

function ProtectedRoute({ children }: { children: JSX.Element }) {
  const { user } = useAuth()
  if (!user) {
    return <Navigate to="/login" replace />
  }
  return children
}

export default function App() {
  const [error, setError] = useState<string | null>(null)

  const closeError = () => setError(null)
  const showError = (message: string) => setError(message)

  return (
    <HashRouter>
      <NextUIProvider>
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <SubgraphsProvider>
              <BookmarksProvider>
                {error && <ErrorAlert message={error} onClose={closeError} />}
                <Toaster
                  position="bottom-right"
                  toastOptions={{
                    duration: 3000,
                    style: {
                      background: '#161616',
                      color: '#ededed',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "DMSans", sans-serif',
                      padding: '10px 14px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                      maxWidth: '360px',
                    },
                    success: {
                      iconTheme: { primary: '#33C2B0', secondary: '#161616' },
                    },
                    error: {
                      iconTheme: { primary: '#ff4444', secondary: '#161616' },
                    },
                  }}
                />
                <Routes>
                  <Route path="/" element={<Navigate to="/login" replace />} />
                  <Route path="/login" element={<Login />} />
                  <Route
                    path="/dashboard/*"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </BookmarksProvider>
              </SubgraphsProvider>
            </AuthProvider>
          </QueryClientProvider>
      </NextUIProvider>
    </HashRouter>
  )
}
